import React, { useState, useEffect } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import PropTypes from 'prop-types';
import { formatDate, insertCommas } from '../../Global/Index.js';
import { useTheme } from '@mui/material';

const SimpleTable = ({ data }) => {
    const [searchInput, setSearchInput] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const theme = useTheme();
    const columns = React.useMemo(
        () => [
            { Header: "Transaction Type", accessor: 'transactionType' },
            { Header: "Date", accessor: 'transactionDate' },
            { Header: "Value Date", accessor: 'transactionValueDate' },
            { Header: "Amount", accessor: 'transactionAmount' },
        ],
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchInput(value);

        const filteredRows = data.filter((row) => {
            return Object.values(row).some((cell) =>
                cell?.toString().toLowerCase().includes(value)
            );
        });

        setFilteredData(filteredRows);
    };

    // Update filteredData whenever data changes
    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        canPreviousPage,
        canNextPage,
        page,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        { columns, data: filteredData, initialState: { pageIndex: 0 } },
        useSortBy,
        usePagination
    );

    // Helper functions to reduce cognitive complexity
    const getSortIndicator = (column) => {
        if (!column.isSorted) return '';
        return column.isSortedDesc ? ' 🔽' : ' 🔼';
    };

    const getRowHoverHandlers = () => ({
        onMouseEnter: (e) => {
            e.currentTarget.style.backgroundColor = theme.palette.primary[800];
            e.currentTarget.style.color = theme.palette.primary.main;
        },
        onMouseLeave: (e) => {
            e.currentTarget.style.backgroundColor = theme.palette.background.alt;
            e.currentTarget.style.color = theme.palette.primary.main;
        }
    });

    const formatCellValue = (cell) => {
        if (cell.column.id === 'transactionDate' || cell.column.id === 'transactionValueDate') {
            return formatDate(cell.value);
        }
        if (cell.column.id === 'transactionAmount') {
            return insertCommas(cell.value);
        }
        return cell.value;
    };

    const getButtonStyle = (canPerformAction) => ({
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        border: 'none',
        padding: '8px 12px',
        margin: '0 5px',
        borderRadius: '5px',
        cursor: canPerformAction ? 'pointer' : 'not-allowed',
        opacity: canPerformAction ? 1 : 0.5,
    });

    const handlePageNavigation = (e) => {
        const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
        gotoPage(pageNumber);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
    };

    if (!data) {
        return (
            <div>
                <h2>Loading...</h2>
            </div>
        );
    }

    const renderTableHeader = () => (
        <thead>
            {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
                    {headerGroup.headers.map((column) => (
                        <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            key={column.id}
                            style={{
                                borderBottom: "1px solid rgb(204,204,204)",
                                paddingBottom: "10px",
                                borderRight: "1px solid rgb(204,204,204)",
                                padding: "8px",
                                textAlign: "center"
                            }}
                        >
                            {column.render('Header')}
                            <span>{getSortIndicator(column)}</span>
                        </th>
                    ))}
                </tr>
            ))}
        </thead>
    );

    const renderTableBody = () => (
        <tbody {...getTableBodyProps()}>
            {page.map((row) => {
                prepareRow(row);
                return (
                    <tr {...row.getRowProps()} key={row.id}
                        style={{
                            backgroundColor: theme.palette.background.alt,
                            color: theme.palette.primary.main,
                        }}
                        {...getRowHoverHandlers()}>
                        <td style={{ border: "1px solid rgb(209,208,208)", padding: "8px", fontWeight: "bold", textAlign: "left" }}>{row.cells[0].value}</td>
                        <td style={{ border: "1px solid rgb(209,208,208)", padding: "8px", textAlign: "center" }}>{formatDate(row.cells[1].value)}</td>
                        <td style={{ border: "1px solid rgb(209,208,208)", padding: "8px", textAlign: "center" }}>{formatDate(row.cells[2].value)}</td>
                        <td style={{ border: "1px solid rgb(209,208,208)", padding: "8px", textAlign: "center" }}>{insertCommas(row.cells[3].value)}</td>
                    </tr>
                );
            })}
        </tbody>
    );

    const renderMobileView = () => (
        <div className="mobile-view">
            {page.map((row) => {
                prepareRow(row);
                return (
                    <div className="card" key={`mobile-${row.id}`} style={{ backgroundColor: theme.palette.background.alt, color: theme.palette.primary.main }}>
                        {row.cells.map((cell) => (
                            <div className="card-item" key={`${row.id}-${cell.column.id}`}>
                                <strong>{cell.column.Header}:</strong>
                                <span className="value">{formatCellValue(cell)}</span>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );

    const renderPaginationControls = () => (
        <div className="pagination" style={{ marginTop: '20px' }}>
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} style={getButtonStyle(canPreviousPage)}>
                {'<<'}
            </button>{' '}
            <button onClick={() => previousPage()} disabled={!canPreviousPage} style={getButtonStyle(canPreviousPage)}>
                {'<'}
            </button>{' '}
            <button onClick={() => nextPage()} disabled={!canNextPage} style={getButtonStyle(canNextPage)}>
                {'>'}
            </button>{' '}
            <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} style={getButtonStyle(canNextPage)}>
                {'>>'}
            </button>{' '}
            <span style={{ color: theme.palette.text.primary }}>
                Page{' '}
                <strong>
                    {pageIndex + 1} of {pageOptions.length}
                </strong>{' '}
            </span>
            <span style={{ color: theme.palette.text.primary }}>
                | Go to page:{' '}
                <input
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={handlePageNavigation}
                    style={{
                        width: '50px',
                        height: '30px',
                        marginRight: '10px',
                        textAlign: 'center',
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '5px',
                    }}
                />
            </span>
            <select
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '5px',
                    padding: '5px',
                }}
            >
                {[10, 20, 30, 40, 50, 60].map((size) => (
                    <option key={size} value={size}>
                        Show {size}
                    </option>
                ))}
            </select>
        </div>
    );

    return (
        <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
            {/* Search Input */}
            <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                <input
                    type="text"
                    value={searchInput}
                    onChange={handleSearch}
                    placeholder="Search..."
                    style={{
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: '2vh',
                        backgroundColor: theme.palette.primary[900],
                        color: theme.palette.primary.main
                    }}
                />
            </div>

            {/* Display 'No data found' if filteredData is empty */}
            {filteredData.length === 0 ? (
                <div style={{ textAlign: 'center', margin: '20px 0', color: 'red' }}>
                    No Data Found
                </div>
            ) : (
                <>
                    {/* Table for larger screens */}
                    <div id="transactionDetails_container">
                        <table {...getTableProps()} aria-label="Financial Data Table" role="table" style={{ borderCollapse: "collapse", width: "100%", border: "1px solid rgb(204,204,204)" }}>
                            {renderTableHeader()}
                            {renderTableBody()}
                        </table>

                        {/* Mobile view rendering */}
                        {renderMobileView()}
                    </div>

                    {/* Pagination Controls */}
                    {renderPaginationControls()}
                </>
            )}

            <style>{`
                @media (max-width: 768px) {
                    table {
                        display:none; /* Hide the table on small screens */
                    }
                    .mobile-view {
                        display:flex;
                        flex-direction: column;
                        margin-top: 20px;
                    }
                    .card {
                        border: 1px solid rgb(204, 204, 204);
                        border-radius: 5px;
                        padding: 10px;
                        margin-bottom: 10px;
                        background-color: #fff;
                    }
                    .card-item {
                        padding-bottom: 5px;
                        display: flex; /* Use flexbox for alignment */
                        justify-content: space-between; /* Space between key and value */
                    }
                    .value {
                        margin-left: 10px; /* Add space between key and value */
                    }
                }
 
                .light tr:nth-child(even) {
                    background-color:#f9f9f9;
                }
             
            `}</style>
        </div>
    );
};

// Define prop types for SimpleTable
SimpleTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            transactionDate: PropTypes.string.isRequired,
            transactionType: PropTypes.string.isRequired,
            transactionValueDate: PropTypes.string.isRequired,
            transactionAmount: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default SimpleTable;